import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    CreateInvestmentRequestRequest,
    CreateInvestmentRequestsBatchRequest,
    UpdateInvestmentRequestRequest,
} from 'client/cip/_openapi/portfolio-optimizer/apis/InvestmentRequestApi';

import { portfolioOptimizerClient } from 'API';

export const useInvestmentRequest = () => {
    return useQuery({
        queryKey: ['investmentRequest', 'list'],
        queryFn: async () => {
            const response = await portfolioOptimizerClient.investmentRequestApi.getInvestmentRequests();
            return response ?? [];
        },
    });
};

export const useInvestmentRequestUpdates = (requestId: number) => {
    return useQuery({
        queryKey: ['investmentRequestUpdate', requestId],
        queryFn: async () => {
            const response = await portfolioOptimizerClient.investmentRequestApi.getInvestmentRequestUpdates({
                requestId,
            });
            return response ?? [];
        },
        enabled: requestId >= 0 && !isNaN(requestId), // Only fetch if we have a valid request ID
    });
};

export const useUpdateInvestmentRequest = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (request: UpdateInvestmentRequestRequest) =>
            portfolioOptimizerClient.investmentRequestApi.updateInvestmentRequest(request),
        onSuccess: (_, request) => {
            // Invalidate the specific investmentRequestUpdate query for the updated requestId
            void queryClient.invalidateQueries({
                queryKey: ['investmentRequestUpdate', request.investmentRequestUpdatePayload.requestId],
            });
            // Also invalidate the general investmentRequest list
            void queryClient.invalidateQueries({ queryKey: ['investmentRequest', 'list'] });
        },
    });
};

export const useCreateInvestmentRequest = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (request: CreateInvestmentRequestRequest) =>
            portfolioOptimizerClient.investmentRequestApi.createInvestmentRequest(request),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['investmentRequest', 'list'] });
            // Submitting a request can change an account's trading status, so refresh account lists too.
            void queryClient.invalidateQueries({ queryKey: ['accounts', 'portfolio-engine'] });
        },
    });
};

export const useCreateInvestmentRequestsBatch = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (request: CreateInvestmentRequestsBatchRequest) =>
            portfolioOptimizerClient.investmentRequestApi.createInvestmentRequestsBatch(request),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['investmentRequest', 'list'] });
            // Submitting requests can change accounts' trading status, so refresh account lists too.
            void queryClient.invalidateQueries({ queryKey: ['accounts', 'portfolio-engine'] });
        },
    });
};
